import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import {
  EmployeeListing,
  MaintenanceRequest,
  MaintenanceRequestResponse,
  MaintenanceRequestService,
  OpenAPI,
  PaginatedMaintenanceResponse,
} from "@/api";
import { RootState } from "@/store/types";
import resolve from "@/common/resolver";
import _ from "lodash";

export type MaintenanceRequestState = {
  requests: PaginatedMaintenanceResponse | null;
  request: MaintenanceRequestResponse | null;
  employees: EmployeeListing | null;
};

const state: MaintenanceRequestState = {
  requests: null,
  request: null,
  employees: null,
};

const getters: GetterTree<MaintenanceRequestState, RootState> = {
  requests: state => state.requests,
  request: state => state.request,
  employees: state => state.employees,
};

const mutations: MutationTree<MaintenanceRequestState> = {
  SET_REQUESTS: (state, requests) => {
    state.requests = requests;
    if (state.requests == null) {
      return;
    }
    state.requests.data = _.orderBy(
      state.requests.data,
      ["createdAt"],
      ["desc"],
    ) as MaintenanceRequestResponse[];
  },
  SET_REQUEST: (state, request) => {
    state.request = request;
  },
  ADD_REQUEST: (state, task) => {
    if (state.requests == null) {
      state.requests = {} as PaginatedMaintenanceResponse;
      state.requests.data = [];
    }
    state.requests?.data.push(task);
    state.requests.data = _.orderBy(
      state.requests.data,
      ["createdAt"],
      ["desc"],
    ) as MaintenanceRequestResponse[];
  },
  REMOVE_REQUEST: (state, request) => {
    let index = state.requests?.data?.findIndex(
      (item: MaintenanceRequestResponse) => item.id == request.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.requests?.data?.splice(index, 1);
    }
    if (state.requests == null) {
      return;
    }
    state.requests.data = _.orderBy(
      state.requests.data,
      ["createdAt"],
      ["desc"],
    ) as MaintenanceRequestResponse[];
  },
  UPDATE_REQUEST: (state, task) => {
    let index = state.requests?.data?.findIndex(
      (item: MaintenanceRequestResponse) => item.id == task.id,
    );
    index = index === undefined ? -1 : index;
    if (index > -1) {
      state.requests?.data?.splice(index, 1);
      state.requests?.data?.push(task);
    }
    if (state.requests == null) {
      return;
    }
    state.requests.data = _.orderBy(
      state.requests.data,
      ["createdAt"],
      ["desc"],
    ) as MaintenanceRequestResponse[];
  },
  SET_MAINTENANCE_EMPLOYEES: (state, employees) => {
    state.employees = employees;
  },
};

const actions: ActionTree<MaintenanceRequestState, RootState> = {
  fetchRequests: async (
    { commit, rootState },
    filter: {
      assignee?: number;
      status?: number;
      limit?: number;
      offset?: number;
      createdAt?: string;
      completedAt?: string;
    },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const requests = await MaintenanceRequestService.getMaintenanceRequests({
        companyId,
        assignee: filter.assignee,
        status: filter.status,
        limit: filter.limit,
        offset: filter.offset,
        createdAt: filter.createdAt,
      });
      commit("SET_REQUESTS", requests);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  addRequest: async (
    { commit, rootState },
    { payload, file }: { payload: MaintenanceRequest; file?: File },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const formData = new FormData();
      if (file) formData.append("image", file, file.name);
      formData.append("requestBody", JSON.stringify(payload));

      const res = await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/companies/${companyId}/maintenance`,
        {
          method: "POST",
          body: formData,
          headers: {
            authorization: `Bearer ${await resolve(OpenAPI.TOKEN)}`,
          },
        },
      )
        .then(async res => {
          const data = await res.json();
          if (!data.assignee) {
            data.assignee = {
              id: 0,
              name: "",
            };
          }
          commit("ADD_REQUEST", data);
          return data;
        })
        .catch(e => {
          if (e instanceof Error) {
            commit("alert/SET_SHOW_ERROR", e.message, { root: true });
          }
        });
      if (res && res.statusText && res.statusText !== "OK") {
        throw new Error(res.message || "Failed to add maintenance request");
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateRequest: async (
    { commit, rootState },
    { payload, file }: { payload: MaintenanceRequest; file?: File },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const formData = new FormData();
      if (file) formData.append("image", file, file.name);
      formData.append("requestBody", JSON.stringify(payload));

      const res = await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/companies/${companyId}/maintenance/${payload.id}`,
        {
          method: "PUT",
          body: formData,
          headers: {
            authorization: `Bearer ${await resolve(OpenAPI.TOKEN)}`,
          },
        },
      )
        .then(async res => {
          const data = await res.json();
          commit("UPDATE_REQUEST", data);
          return data;
        })
        .catch(e => {
          if (e instanceof Error) {
            commit("alert/SET_SHOW_ERROR", e.message, { root: true });
          }
        });
      if (res && res.statusText && res.statusText !== "OK") {
        throw new Error(res.message || "Failed to add maintenance request");
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateRequestStatus: async (
    { commit, rootState },
    { payload, file }: { payload: MaintenanceRequest; file?: File },
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const formData = new FormData();
      if (file) formData.append("completionImage", file, file.name);
      formData.append("requestBody", JSON.stringify(payload));

      const res = await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/companies/${companyId}/maintenance/${payload.id}/status`,
        {
          method: "PUT",
          body: formData,
          headers: {
            authorization: `Bearer ${await resolve(OpenAPI.TOKEN)}`,
          },
        },
      )
        .then(async res => {
          const data = await res.json();
          if (!data.assignee) {
            data.assignee = {
              id: 0,
              name: "",
            };
          }
          commit("UPDATE_REQUEST", data);
          return data;
        })
        .catch(e => {
          if (e instanceof Error) {
            commit("alert/SET_SHOW_ERROR", e.message, { root: true });
          }
        });
      if (res && res.statusText && res.statusText !== "OK") {
        throw new Error(res.message || "Failed to add maintenance request");
      }
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  updateRequestAssignee: async (
    { commit, rootState },
    requestBody: MaintenanceRequest,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const request = await MaintenanceRequestService.updateMaintenanceRequestAssignee(
        {
          companyId,
          maintenanceId: requestBody.id,
          requestBody,
        },
      );
      commit("UPDATE_REQUEST", request);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  deleteRequest: async (
    { commit, rootState },
    requestBody: MaintenanceRequest,
  ) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      await MaintenanceRequestService.deleteMaintenanceRequest({
        companyId,
        maintenanceId: requestBody.id,
        requestBody,
      });
      commit("REMOVE_REQUEST", requestBody);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
  getMaintenanceEmployees: async ({ commit, rootState }) => {
    const companyId = rootState.appContext.company?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }

    try {
      const employees = await MaintenanceRequestService.getMaintenanceEmployees(
        { companyId },
      );
      commit("SET_MAINTENANCE_EMPLOYEES", employees);
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const maintenanceRequest: Module<MaintenanceRequestState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
